import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookOpen, Clock, CheckCircle2, XCircle, GraduationCap, ChevronRight, Award } from "lucide-react";

export default function Learning() {
  const [assignments, setAssignments] = useState([]);
  const [taking, setTaking] = useState(null); // { assignment, course }
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/course-assignments"); setAssignments(data); }
    catch { toast.error("Failed to load courses"); }
  };
  useEffect(() => { load(); }, []);

  const start = async (a) => {
    try {
      const [c] = await Promise.all([api.get(`/courses/${a.course_id}`), api.post(`/course-assignments/${a.id}/start`)]);
      setTaking({ assignment: a, course: c.data });
      setAnswers({}); setResult(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submit = async () => {
    if (!taking) return;
    const quiz = taking.course.quiz || [];
    if (quiz.length && quiz.some(q => answers[q.id] === undefined)) return toast.error("Please answer all questions");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/course-assignments/${taking.assignment.id}/submit`, { answers });
      setResult(data);
      if (data.passed) toast.success(`Passed with ${data.score}%!`);
      else toast.error(`Failed: ${data.score}% (need ${data.pass_score}%)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSubmitting(false); }
  };

  const kpis = {
    total: assignments.length,
    completed: assignments.filter(a => a.status === "completed").length,
    in_progress: assignments.filter(a => a.status === "in_progress").length,
    failed: assignments.filter(a => a.status === "failed").length,
  };

  if (taking) return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="course-runner">
      <button onClick={() => { setTaking(null); setResult(null); }} className="text-xs uppercase tracking-wider font-semibold text-gray-600 mb-4">← Back to My Learning</button>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Course · {taking.course.category}</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{taking.course.title}</h1>
        <p className="text-sm text-gray-600 mt-1">{taking.course.description}</p>
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 uppercase tracking-wider">
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {taking.course.duration_minutes}m</span>
          <span>Pass mark: {taking.course.pass_score}%</span>
          <span>{(taking.course.quiz || []).length} question(s)</span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 p-6 mb-6">
        <h2 className="font-display text-lg font-bold tracking-tight mb-3">Content</h2>
        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed" data-testid="course-content">{taking.course.content}</pre>
      </div>

      {(taking.course.quiz || []).length > 0 && (
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <h2 className="font-display text-lg font-bold tracking-tight mb-4">Quiz</h2>
          <div className="space-y-6" data-testid="quiz">
            {taking.course.quiz.map((q, qi) => (
              <div key={q.id} data-testid={`quiz-q-${qi}`}>
                <div className="text-sm font-semibold mb-3">Q{qi + 1}. {q.question}</div>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => (
                    <label key={oi} className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${answers[q.id] === oi ? "border-[#01ADEF] bg-[#F0F7FF]" : "border-gray-200 hover:bg-gray-50"}`} data-testid={`quiz-q-${qi}-opt-${oi}`}>
                      <input type="radio" name={q.id} checked={answers[q.id] === oi} onChange={() => setAnswers({ ...answers, [q.id]: oi })} disabled={!!result} />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result && (
        <div className={`border-l-4 p-5 mb-6 ${result.passed ? "border-[#059669] bg-[#F0FDF4]" : "border-[#DC2626] bg-[#FEF2F2]"}`} data-testid="quiz-result">
          <div className="flex items-center gap-3 mb-2">
            {result.passed ? <CheckCircle2 className="w-6 h-6 text-[#059669]" /> : <XCircle className="w-6 h-6 text-[#DC2626]" />}
            <div className="font-display text-xl font-bold">{result.passed ? "Course Completed" : "Not Passed"}</div>
          </div>
          <div className="text-sm">Score: <b>{result.score}%</b> (pass mark {result.pass_score}%)</div>
        </div>
      )}

      {!result && (
        <div className="flex items-center gap-3">
          <Button data-testid="submit-quiz-btn" onClick={submit} disabled={submitting} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{submitting ? "Submitting…" : "Submit &amp; Complete"}</Button>
          <Button variant="outline" className="rounded-full" onClick={() => { setTaking(null); setResult(null); }}>Cancel</Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 md:p-8" data-testid="learning-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Training</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">My Learning</h1>
        <p className="text-sm text-gray-500 mt-1">Assigned courses, in-progress work, and completed training records.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <MiniKpi label="Assigned" value={kpis.total} icon={BookOpen} accent="bg-[#01ADEF]" testid="learning-kpi-total" />
        <MiniKpi label="Completed" value={kpis.completed} icon={CheckCircle2} accent="bg-[#059669]" testid="learning-kpi-completed" />
        <MiniKpi label="In Progress" value={kpis.in_progress} icon={Clock} accent="bg-[#D97706]" testid="learning-kpi-inprogress" />
        <MiniKpi label="Failed" value={kpis.failed} icon={XCircle} accent="bg-[#DC2626]" testid="learning-kpi-failed" />
      </div>

      <div className="space-y-3" data-testid="assignments-list">
        {assignments.length === 0 ? (
          <div className="bg-white border border-gray-200 p-10 text-center text-sm text-gray-500">
            <GraduationCap className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            No training assigned yet.
          </div>
        ) : assignments.map(a => {
          const canRetake = a.status === "failed";
          const canStart = a.status === "assigned" || a.status === "in_progress" || canRetake;
          return (
            <div key={a.id} className="bg-white border border-gray-200 p-5 flex items-center gap-5 hover:border-gray-300 transition-colors" data-testid={`assignment-${a.id}`}>
              <div className="w-12 h-12 bg-[#F0F7FF] border border-[#DBEAFE] flex items-center justify-center shrink-0"><BookOpen className="w-5 h-5 text-[#01ADEF]" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-base">{a.course_title || "Untitled"}</div>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border bg-gray-100 border-gray-300 text-gray-700">{a.course_category}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.course_duration || 30}m</span>
                  <span>{a.quiz_count || 0} questions</span>
                  {a.due_date && <span>Due {a.due_date}</span>}
                  {a.score != null && <span className="flex items-center gap-1"><Award className="w-3 h-3" />Score: {a.score}%</span>}
                </div>
              </div>
              <StatusPill s={a.status} />
              {canStart && (
                <Button data-testid={`start-${a.id}`} onClick={() => start(a)} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">
                  {a.status === "in_progress" ? "Resume" : canRetake ? "Retake" : "Start"} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {a.status === "completed" && (
                <Button data-testid={`review-${a.id}`} onClick={() => start(a)} variant="outline" className="rounded-full border-gray-300">Review</Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const MiniKpi = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="bg-white border border-gray-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</span>
      <div className={`w-7 h-7 ${accent} flex items-center justify-center`}><Icon className="w-3.5 h-3.5 text-white" strokeWidth={2.5} /></div>
    </div>
    <div className="font-display text-3xl font-bold tracking-tight">{value}</div>
  </div>
);
const StatusPill = ({ s }) => {
  const map = {
    assigned: ["bg-[#E0F2FE]", "text-[#075985]", "border-[#7DD3FC]", "Assigned"],
    in_progress: ["bg-[#FEF3C7]", "text-[#92400E]", "border-[#FCD34D]", "In Progress"],
    completed: ["bg-[#D1FAE5]", "text-[#065F46]", "border-[#6EE7B7]", "Completed"],
    failed: ["bg-[#FEE2E2]", "text-[#B91C1C]", "border-[#FCA5A5]", "Failed"],
  };
  const [bg, tx, br, label] = map[s] || map.assigned;
  return <span className={`text-xs font-bold px-2 py-1 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{label}</span>;
};
